import { BaseChart } from './BaseChart.js';

export class BarChart extends BaseChart {
    constructor({
        // DATOS
        /**
         * Array de objetos con los datos de las barras.
         * Cada objeto debe tener:
         * - xKey (por defecto 'value'): valor numérico de la barra
         * - yKey (por defecto 'name'): nombre/categoría
         * 
         * Ejemplo:
         * [
         *   { name: 'Enero', value: 120 },
         *   { name: 'Febrero', value: 200 },
         *   { name: 'Marzo', value: 150 }
         * ]
         */
        data = [],                      // Array de objetos con los datos de las barras
        xKey = 'value',                 // Propiedad usada para valores
        yKey = 'name',                  // Propiedad usada para categorías

        // ORIENTACIÓN
        orientation = 'horizontal',     // 'horizontal' o 'vertical'

        // COLORES Y ESTILOS DE BARRAS
        barColor = '#000C40',           // Color sólido de la barra
        barBorderColor = null,          // Color del borde
        barBorderWidth = 0,             // Grosor del borde
        barBorderRadius = 0,            // Bordes redondeados
        barWidth = null,                // Ancho fijo de la barra
        barMaxWidth = null,             // Ancho máximo
        barMinHeight = 0,               // Altura mínima de barra horizontal
        barGap = '30%',                 // Espacio entre barras
        barCategoryGap = '20%',         // Espacio entre categorías
        colorPalette = null,            // Paleta de colores opcional
        useGradient = false,            // Usar gradiente
        gradientColor = ['#667eea', '#764ba2'], // Colores gradiente

        // LABELS
        showLabels = true,              // Mostrar etiquetas
        labelPosition = null,           // 'top', 'inside', etc.
        labelFormatter = null,          // Función o plantilla de etiquetas
        labelFontSize = 12,             // Tamaño de fuente de etiquetas
        labelColor = '#000',            // Color de etiquetas
        labelFontWeight = 'normal',     // Grosor de fuente
        axisFontSize = 10,              // Tamaño de letra para ejes X e Y

        // FONDO DE BARRAS
        showBackground = false,         // Mostrar fondo de barra
        backgroundColor = 'rgba(180, 180, 180, 0.2)',   // Color fondo

        // EJES
        xAxisName = '',                 // Nombre eje X
        yAxisName = '',                 // Nombre eje Y
        xAxisNameGap = 30,              // Separación nombre eje X
        yAxisNameGap = 110,             // Separación nombre eje Y
        xAxisRotate = 0,                // Rotación etiquetas eje X
        yAxisRotate = 0,                // Rotación etiquetas eje Y
        showXAxisLine = true,           // Mostrar línea del eje X
        showYAxisLine = true,           // Mostrar línea del eje Y
        showXAxisSplitLine = false,     // Mostrar líneas de división eje X
        showYAxisSplitLine = true,      // Mostrar líneas de división eje Y
        xAxisLabelFormatter = null,     // Función/plantilla etiquetas X
        yAxisLabelFormatter = null,     // Función/plantilla etiquetas Y

        // ORDENAMIENTO
        sortAscending = false,          // Orden ascendente
        sortDescending = false,         // Orden descendente

        // INTERACTIVIDAD
        selectedMode = false,           // false, 'single', 'multiple'
        selectedOffset = 5,             // Desplazamiento al seleccionar

        // PROPIEDADES BASE
        ...baseProps
    } = {}) {
        // Constructor de la clase base para sobreescribir propiedades
        super({
            ...baseProps,
            tooltipTrigger: baseProps.tooltipTrigger || 'axis',
            tooltipAxisPointer: baseProps.tooltipAxisPointer || 'shadow',
            toolboxFeatures: baseProps.toolboxFeatures || ['magicType', 'dataZoom', 'restore', 'saveAsImage'],
            gridLeft: baseProps.gridLeft || '4%',
            gridRight: baseProps.gridRight || '10%',
        });

        // Propiedades específicas de BarChart
        this.data = data;
        this.xKey = xKey;
        this.yKey = yKey;
        this.orientation = orientation;
        this.barColor = barColor;
        this.barBorderColor = barBorderColor;
        this.barBorderWidth = barBorderWidth;
        this.barBorderRadius = barBorderRadius;
        this.barWidth = barWidth;
        this.barMaxWidth = barMaxWidth;
        this.barMinHeight = barMinHeight;
        this.barGap = barGap;
        this.barCategoryGap = barCategoryGap;
        this.barColorPalette = colorPalette; // Nombre diferente al declarado en BaseChart
        this.useGradient = useGradient;
        this.gradientColor = gradientColor;
        this.showLabels = showLabels;
        this.labelPosition = labelPosition ?? (orientation === 'horizontal' ? 'right' : 'top');
        this.labelFormatter = labelFormatter;
        this.labelFontSize = labelFontSize;
        this.labelColor = labelColor;
        this.labelFontWeight = labelFontWeight;
        this.showBackground = showBackground;
        this.backgroundColor = backgroundColor;
        this.axisFontSize = axisFontSize;
        this.xAxisName = xAxisName;
        this.yAxisName = yAxisName;
        this.xAxisNameGap = xAxisNameGap;
        this.yAxisNameGap = yAxisNameGap;
        this.xAxisRotate = xAxisRotate;
        this.yAxisRotate = yAxisRotate;
        this.showXAxisLine = showXAxisLine;
        this.showYAxisLine = showYAxisLine;
        this.showXAxisSplitLine = showXAxisSplitLine;
        this.showYAxisSplitLine = showYAxisSplitLine;
        this.xAxisLabelFormatter = xAxisLabelFormatter;
        this.yAxisLabelFormatter = yAxisLabelFormatter;
        this.sortAscending = sortAscending;
        this.sortDescending = sortDescending;
        this.selectedMode = selectedMode;
        this.selectedOffset = selectedOffset;

        // Configurar tipos de ejes según orientación
        if (orientation === 'horizontal') {
            this.xAxisType = 'value';
            this.yAxisType = 'category';
            this.yAxisRotate = 30;
        } else {
            this.xAxisType = 'category';
            this.yAxisType = 'value';
            this.xAxisRotate = 30;
        }
    }

    prepareData() {
        const chartData = [...this.data];
        if (this.sortDescending) {
            chartData.sort((a, b) => (b[this.xKey] ?? 0) - (a[this.xKey] ?? 0));
        } else if (this.sortAscending) {
            chartData.sort((a, b) => (a[this.xKey] ?? 0) - (b[this.xKey] ?? 0));
        }
        return chartData;
    }

    /**
     * Obtiene el color de la barra según configuración.
     * Prioridad: barColorPalette > gradiente > color sólido
     */
    getBarColor(index) {
        // 1. Usar paleta de colores específica de barras si está definida
        if (this.barColorPalette && this.barColorPalette.length > 0) {
            return this.barColorPalette[index % this.barColorPalette.length];
        }

        // 2. Aplicar gradiente si está habilitado
        if (this.useGradient) {
            return {
                type: 'linear',
                x: this.orientation === 'horizontal' ? 1 : 0,
                y: this.orientation === 'horizontal' ? 0 : 1,
                x2: 0,
                y2: 0,
                colorStops: [
                    { offset: 0, color: this.gradientColor[0] },
                    { offset: 1, color: this.gradientColor[1] }
                ]
            };
        }

        // 3. Color sólido por defecto
        return this.barColor;
    }

    transformToStacked() {
        this.barGap = '0%';
        this.barCategoryGap = '20%';
        return this;
    }

    transformToRacing() {
        this.sortDescending = true;
        this.animationDuration = 500;
        this.animationDelay = (idx) => idx * 50;
        this.showLabels = true;
        this.labelPosition = this.orientation === 'horizontal' ? 'right' : 'top';
        return this;
    }

    transformToGradient(colors = null) {
        this.useGradient = true;
        if (colors) this.gradientColor = colors;
        return this;
    }

    transformToModern() {
        this.barBorderRadius = 8;
        this.barMaxWidth = 50;
        this.showBackground = true;
        this.backgroundColor = 'rgba(180, 180, 180, 0.1)';
        this.emphasis.itemStyle.shadowBlur = 15;
        return this;
    }

    getOptions() {
        const chartData = this.prepareData();
        const isHorizontal = this.orientation === 'horizontal';

        const options = {
            title: this.getTitleConfig(),
            containLabel: true,
            tooltip: this.getTooltipConfig((params) => {
                const p = Array.isArray(params) ? params[0] : params;
                const prettyName = p.name ?? 'Sin nombre';
                const value = p.value ?? 0;
                return `
                    <div style="padding: 8px;">
                        <strong style="font-size: 14px;">${prettyName}</strong><br/>
                        <span style="color: #ddd;">Valor:</span> <strong>${value.toLocaleString()}</strong>
                    </div>
                `;
            }),
            grid: this.getGridConfig(),
            xAxis: {
                type: this.xAxisType,
                name: this.xAxisName,
                nameLocation: 'middle',
                nameGap: this.xAxisNameGap,
                data: !isHorizontal ? chartData.map(d => d[this.yKey] ?? 'Sin nombre') : undefined,
                nameTextStyle: {
                    fontSize: this.axisFontSize
                },
                axisLabel: {
                    rotate: this.xAxisRotate,
                    formatter: this.xAxisLabelFormatter,
                    fontSize: this.axisFontSize
                },
                axisLine: { show: this.showXAxisLine },
                splitLine: { show: this.showXAxisSplitLine },
            },
            yAxis: {
                type: this.yAxisType,
                name: this.yAxisName,
                nameLocation: 'middle',
                nameGap: this.yAxisNameGap,
                data: isHorizontal ? chartData.map(d => d[this.yKey] ?? 'Sin nombre') : undefined,
                nameTextStyle: {
                    fontSize: this.axisFontSize,
                },
                axisLabel: {
                    rotate: this.yAxisRotate,
                    formatter: this.yAxisLabelFormatter,
                    overflow: 'truncate',  // Trunca el texto si es muy largo
                    width: 100,            // Ancho máximo del label
                    ellipsis: '...',
                    fontSize: this.axisFontSize,
                },
                axisLine: { show: this.showYAxisLine },
                splitLine: { show: this.showYAxisSplitLine },
            },
            series: [
                {
                    id: this.seriesId,
                    name: this.seriesName,
                    type: 'bar',
                    universalTransition: this.universalTransition,
                    animationDurationUpdate: this.animationDurationUpdate,
                    animationDuration: this.animationDuration,
                    animationEasing: this.animationEasing,
                    animationDelay: typeof this.animationDelay === 'function'
                        ? this.animationDelay
                        : this.animationDelay,
                    barWidth: this.barWidth,
                    barMaxWidth: this.barMaxWidth,
                    barMinHeight: this.barMinHeight,
                    barGap: this.barGap,
                    barCategoryGap: this.barCategoryGap,
                    selectedMode: this.selectedMode,
                    data: chartData.map((d, idx) => ({
                        name: d[this.yKey] ?? 'Sin nombre',
                        value: d[this.xKey] ?? 0,
                        itemStyle: {
                            color: this.getBarColor(idx),
                            borderColor: this.barBorderColor,
                            borderWidth: this.barBorderWidth,
                            borderRadius: this.barBorderRadius,
                        }
                    })),
                    showBackground: this.showBackground,
                    backgroundStyle: {
                        color: this.backgroundColor,
                        borderRadius: this.barBorderRadius,
                    },
                    label: {
                        show: this.showLabels,
                        position: this.labelPosition,
                        fontSize: this.labelFontSize,
                        color: this.labelColor,
                        fontWeight: this.labelFontWeight,
                        formatter: this.labelFormatter || ((params) => params.value.toLocaleString()),
                    },
                    emphasis: this.emphasis,
                    blur: this.blur,
                    select: this.selectedMode ? {
                        itemStyle: {
                            borderColor: '#000',
                            borderWidth: 2,
                        }
                    } : undefined,
                }
            ],
        };

        // Agregar toolbox
        const toolbox = this.getToolboxConfig();
        if (toolbox.show) {
            options.toolbox = toolbox;
        }

        // Agregar dataZoom
        const dataZoom = this.getDataZoomConfig([0], !isHorizontal);
        if (dataZoom) {
            options.dataZoom = dataZoom;
        }

        return options;
    }
}